/**
 * WorkoutPickerDrawer — unified file/workout source picker for the running module.
 *
 * Renders a right-side drawer with tabs:
 *   Upload      — drag-drop / browse .fit file
 *   My Files    — user's saved FIT files (DataGrid, quick-filter, optional delete)
 *   Community   — shared community workouts (DataGrid, quick-filter)
 *   [extraTabs] — caller-supplied tabs, rendered as a DataGrid or, when the
 *                 tab provides render(), by the tab itself
 *
 * Props
 * -----
 * open               boolean
 * onClose            () => void
 * onSelect           (source) => void
 *   source shapes:
 *     { type: 'upload',      file: File }
 *     { type: 'myfile',      fileId: number, record: Object }
 *     { type: 'community',   sharedId: number, record: Object }
 *     { type: 'race',        raceId: number, record: Object }
 *     { type: <extraTab.id>, ...extraTab.getSource(row) }
 *
 * title              string                       drawer header text
 * tabs               string[]                     which built-in tabs to show
 *                    default: ['upload','myFiles','community']
 *                    also supports: 'races'
 * extraTabs          Array<{
 *                      id:        string,
 *                      label:     string,
 *                      rows:      Object[],       // must have .id field
 *                      columns:   GridColDef[],
 *                      loading:   boolean,
 *                      getSource: (row) => Object, // builds the source object
 *                      render?:   ({selectedId, onSelect, onConfirm}) => ReactNode
 *                    }>
 * footer             ReactNode   content above the dismiss/action buttons
 * dismissLabel       string      renames the dismiss button, e.g. 'Exit'. Default
 *                    'Cancel' — right for a picker, where leaving does abandon a
 *                    choice. A caller that renames it is saying this drawer is a
 *                    place you leave rather than a choice you abort, so the
 *                    button also gets a close icon.
 * actionLabel        string      default: 'Select'
 * allowDeleteMyFiles boolean     show delete icon in My Files rows
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Drawer, Box, Typography, Tabs, Tab, Button, IconButton,
  CircularProgress, Alert, useTheme, useMediaQuery,
  FormControl, InputLabel, Select, MenuItem, TextField, Chip, Snackbar,
} from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import ShareIcon from '@mui/icons-material/Share';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { attentionPulse, pulseAnimation, PULSE_TOTAL_MS } from '../../utils/attentionPulse';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  getMyRunningFitFiles,
  getUserRunningFitFilesWithSharing,
  listSharedRunningWorkouts,
  deleteMyRunningFile,
  unshareRunningWorkout,
  getRunningRaces,
  listRunningPredictions,
  deleteRunningPrediction,
  getRunningProfile,
  updateRunningProfile,
} from '../../api/runningApi';
import { getMyClubRunningPredictions, shareClubPrediction } from '../../api/clubApi';
import { formatDistance, formatPace } from '../../utils/units';
import ShareWorkoutDialog from './ShareWorkoutDialog';
import ShareToClubDialog from './ShareToClubDialog';
import RaceDetailDialog from './RaceDetailDialog';
import { garminDeviceLabel } from '../garmin/GarminAttribution';

// ── Module-level helpers ─────────────────────────────────────────────────
const fmtFinishTime = (secs) => {
  if (!secs) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

// ── Quick-filter toolbar ──────────────────────────────────────────────────
function SearchToolbar() {
  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <GridToolbarQuickFilter debounceMs={200} fullWidth size="small" />
    </Box>
  );
}

// ── Component ─────────────────────────────────────────────────────────────
export default function WorkoutPickerDrawer({
  open,
  onClose,
  onSelect,
  title = 'Select Workout',
  tabs = ['upload', 'myFiles', 'community'],
  extraTabs = [],
  footer,
  actionLabel = 'Select',
  dismissLabel = null,
  allowDeleteMyFiles = false,
  allowShareMyFiles = false,
  allowMarkSecondaryRef = false,  // show "2nd ref" toggle in My Files rows (deep prediction)
  // Called when the user wants to run a multi-file prediction with the files
  // they have just marked. Without it the footer button is not offered — marking
  // still works, it just has no shortcut from here.
  onMultiFilePredict = null,
  // Bumped by a caller that sent the user here specifically to mark runs. The
  // bookmark column is 78px of icons in the corner of a table — being told to
  // "mark runs" and not being shown where is most of the confusion, so a bump
  // pulses that column.
  highlightMarkSignal = 0,
  predictionsFilter = null,  // optional (row) => bool to filter predictions list
  clubPredictionsFilter = null,  // same, for the club predictions tab
  // Narrow "My Predictions" to one race at the source. The list is capped, so a
  // client-side filter can only sieve what the cap already let through.
  predictionsRaceId = null,
  // One race only: the race is already in the dialog title, so the Race and Dist
  // columns say the same thing on every row. Dropping them leaves room for the
  // reference files, which is what actually distinguishes one prediction here.
  predictionsSingleRace = false,
  onTabChange = null,        // optional (tabId: string) => void — called on tab switch
  defaultTab = null,         // optional tab id to select when drawer opens
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const pulse = attentionPulse(colors);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  // Build ordered tab list — respects the order of the `tabs` array
  const BUILT_IN_TAB_LABELS = {
    upload:          t('uploadTab',          'Upload'),
    gpxUpload:       t('uploadGpxTab',       'Upload GPX'),
    myFiles:         t('myFilesTab',         'My Running Files'),
    community:       t('communityTab',       'Community'),
    races:           t('racesTab',           'Races'),
    predictions:     t('myPredictionsTab',   'My Predictions'),
    clubPredictions: t('clubPredictionsTab', 'Club Predictions'),
  };
  // `tabs` may name an extra tab's id as well as a built-in one, which is the
  // only way to interleave them — appending every extra at the end forced
  // Garmin and Club Files to sit after Community whatever the caller wanted.
  // Extras not named in `tabs` are still appended, so older callers are
  // unaffected.
  const extraById = Object.fromEntries(extraTabs.map(tab => [tab.id, tab]));
  const allTabs = [
    ...tabs.map((id) => {
      if (BUILT_IN_TAB_LABELS[id]) return { id, label: BUILT_IN_TAB_LABELS[id] };
      if (extraById[id]) return { id, label: extraById[id].label };
      return null;
    }).filter(Boolean),
    ...extraTabs.filter(tab => !tabs.includes(tab.id)).map(tab => ({ id: tab.id, label: tab.label })),
  ];

  const [tabIndex, setTabIndex] = useState(0);
  const currentTabId = allTabs[tabIndex]?.id;

  // Upload (FIT)
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // GPX Upload
  const [gpxUploadFile, setGpxUploadFile] = useState(null);
  const [gpxDragOver, setGpxDragOver] = useState(false);
  const gpxInputRef = useRef(null);

  // My Files
  const [myFiles, setMyFiles] = useState([]);
  const [myFilesLoading, setMyFilesLoading] = useState(false);
  const [myFilesLoaded, setMyFilesLoaded] = useState(false);
  // Rows with a delete request in flight. The call takes a second or two and
  // the only sign it had started was the row eventually vanishing, so the icon
  // becomes a spinner and stops accepting clicks until it comes back.
  const [deletingFileIds, setDeletingFileIds] = useState([]);
  const [deletingPredictionIds, setDeletingPredictionIds] = useState([]);

  // Secondary reference files (deep prediction) — only when allowMarkSecondaryRef
  const [secondaryRefIds, setSecondaryRefIds] = useState([]);
  const [secondaryRefLoaded, setSecondaryRefLoaded] = useState(false);
  const [secondaryRefSaving, setSecondaryRefSaving] = useState(false);
  // The race-prediction button used to appear only once runs were marked, so a
  // new user never saw the feature and had nothing to click to find out about
  // it. The button is always there now; pressing it with nothing marked pulses
  // the bookmark column instead of starting a prediction. That pulse is the
  // whole prompt — a paragraph of instructions stood here and went unread.
  const [markColHighlight, setMarkColHighlight] = useState(false);

  // Community
  const [communityRows, setCommunityRows] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityLoaded, setCommunityLoaded] = useState(false);

  // Races
  const [racesRows, setRacesRows] = useState([]);
  const [racesLoading, setRacesLoading] = useState(false);
  const [racesLoaded, setRacesLoaded] = useState(false);

  // Predictions
  const [predictionsRows, setPredictionsRows] = useState([]);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionsLoaded, setPredictionsLoaded] = useState(false);

  // Club Predictions
  const [clubPredictionsRows, setClubPredictionsRows] = useState([]);
  const [clubPredictionsLoading, setClubPredictionsLoading] = useState(false);
  const [clubPredictionsLoaded, setClubPredictionsLoaded] = useState(false);
  const [racesCountry, setRacesCountry] = useState('');
  const [racesSearch, setRacesSearch] = useState('');
  const [raceDetailRow, setRaceDetailRow] = useState(null);
  const racesSearchRef = useRef(null);

  // Per-tab selection  tabId → rowId
  const [selectionMap, setSelectionMap] = useState({});

  const [error, setError] = useState(null);

  // Share dialog
  const [shareDialogFile, setShareDialogFile] = useState(null);
  // Share a prediction to a club (My Predictions tab)
  const [sharePredictionRow, setSharePredictionRow] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [snack, setSnack] = useState('');

  // Focus the races search field when the races tab becomes active
  useEffect(() => {
    if (open && currentTabId === 'races') {
      const timer = setTimeout(() => racesSearchRef.current?.focus(), 150);
      return () => clearTimeout(timer);
    }
  }, [open, currentTabId]);

  // Reset ephemeral state when drawer closes (keep cached list data except predictions)
  useEffect(() => {
    if (!open) {
      setUploadFile(null);
      setGpxUploadFile(null);
      setSelectionMap({});
      setTabIndex(0);
      setError(null);
      setDragOver(false);
      setGpxDragOver(false);
      setRacesCountry('');
      setRacesSearch('');
      // Reload predictions and races on next open so new data appears immediately
      setPredictionsLoaded(false);
      setClubPredictionsLoaded(false);
      setRacesLoaded(false);
      setSecondaryRefLoaded(false);
      setMarkColHighlight(false);
    } else {
      // Jump to defaultTab when opening
      if (defaultTab) {
        const idx = allTabs.findIndex(t => t.id === defaultTab);
        if (idx >= 0) setTabIndex(idx);
      }
      // Notify parent of the initial tab when drawer opens
      const initialTab = defaultTab
        ? (allTabs.find(t => t.id === defaultTab)?.id ?? allTabs[0]?.id)
        : allTabs[0]?.id;
      if (onTabChange) onTabChange(initialTab ?? null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw the eye to the bookmark column. Cleared once the pulse has played, so
  // pressing the same control again replays it instead of doing nothing.
  const markPulseTimer = useRef(null);
  const pulseMarkColumn = () => {
    if (markPulseTimer.current) clearTimeout(markPulseTimer.current);
    setMarkColHighlight(false);
    // Re-setting the same value does not restart a CSS animation, so let the
    // cleared state paint first.
    requestAnimationFrame(() => setMarkColHighlight(true));
    markPulseTimer.current = setTimeout(() => setMarkColHighlight(false), PULSE_TOTAL_MS);
  };
  useEffect(() => () => {
    if (markPulseTimer.current) clearTimeout(markPulseTimer.current);
  }, []);

  useEffect(() => {
    if (highlightMarkSignal > 0) pulseMarkColumn();
  }, [highlightMarkSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load built-in tab data on first visit
  useEffect(() => {
    if (!open) return;
    setError(null);

    if (currentTabId === 'myFiles' && !myFilesLoaded && !myFilesLoading) {
      setMyFilesLoading(true);
      const loader = allowShareMyFiles ? getUserRunningFitFilesWithSharing : getMyRunningFitFiles;
      loader()
        .then(data => {
          setMyFiles(Array.isArray(data) ? data : data.files || []);
          setMyFilesLoaded(true);
        })
        .catch(() => setError(t('failedToLoadFiles', 'Failed to load your files')))
        .finally(() => setMyFilesLoading(false));
    }

    if (currentTabId === 'myFiles' && allowMarkSecondaryRef && !secondaryRefLoaded) {
      getRunningProfile()
        .then(data => {
          setSecondaryRefIds(Array.isArray(data?.secondary_fit_file_ids) ? data.secondary_fit_file_ids : []);
          setSecondaryRefLoaded(true);
        })
        .catch(() => { /* non-fatal: toggle just starts empty */ });
    }

    if (currentTabId === 'community' && !communityLoaded && !communityLoading) {
      setCommunityLoading(true);
      listSharedRunningWorkouts()
        .then(data => {
          setCommunityRows(Array.isArray(data) ? data : data.workouts || []);
          setCommunityLoaded(true);
        })
        .catch(() => setError(t('failedToLoadCommunity', 'Failed to load community workouts')))
        .finally(() => setCommunityLoading(false));
    }

    if (currentTabId === 'races' && !racesLoaded && !racesLoading) {
      setRacesLoading(true);
      getRunningRaces({ page_size: 200 })
        .then(data => {
          setRacesRows(data.races || []);
          setRacesLoaded(true);
        })
        .catch(() => setError(t('failedToLoadRaces', 'Failed to load races')))
        .finally(() => setRacesLoading(false));
    }

    if (currentTabId === 'predictions' && !predictionsLoaded && !predictionsLoading) {
      setPredictionsLoading(true);
      listRunningPredictions(predictionsRaceId)
        .then(data => {
          const filtered = predictionsFilter ? (data || []).filter(predictionsFilter) : (data || []);
          setPredictionsRows(filtered.map((p, i) => ({ ...p, id: p.prediction_session_id || i })));
          setPredictionsLoaded(true);
        })
        .catch(() => setError(t('failedToLoadPredictions', 'Failed to load predictions')))
        .finally(() => setPredictionsLoading(false));
    }

    if (currentTabId === 'clubPredictions' && !clubPredictionsLoaded && !clubPredictionsLoading) {
      setClubPredictionsLoading(true);
      getMyClubRunningPredictions()
        .then(data => {
          const rows = Array.isArray(data) ? data : [];
          const filtered = clubPredictionsFilter ? rows.filter(clubPredictionsFilter) : rows;
          setClubPredictionsRows(
            filtered.map((p, i) => ({
              ...p,
              id: p.prediction_id || i,
              prediction_session_id: p.prediction_id,
            }))
          );
          setClubPredictionsLoaded(true);
        })
        .catch(() => setError(t('failedToLoadClubPredictions', 'Failed to load club predictions')))
        .finally(() => setClubPredictionsLoading(false));
    }
  }, [open, currentTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleTabChange = (_, newIndex) => {
    setTabIndex(newIndex);
    setError(null);
    if (onTabChange) onTabChange(allTabs[newIndex]?.id ?? null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.toLowerCase().endsWith('.fit')) {
      setUploadFile(f);
      setError(null);
    } else {
      setError(t('pleaseDropFitFile', 'Please drop a .fit file'));
    }
  };

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) { setUploadFile(f); setError(null); }
  };

  const handleGpxDrop = (e) => {
    e.preventDefault();
    setGpxDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.toLowerCase().endsWith('.gpx')) {
      setGpxUploadFile(f);
      setError(null);
    } else {
      setError(t('pleaseDropGpxFile', 'Please drop a .gpx file'));
    }
  };

  const handleGpxInput = (e) => {
    const f = e.target.files[0];
    if (f) { setGpxUploadFile(f); setError(null); }
  };

  const handleDeletePrediction = async (predictionId, e) => {
    e.stopPropagation();
    if (deletingPredictionIds.includes(predictionId)) return;
    setDeletingPredictionIds(prev => [...prev, predictionId]);
    setError(null);
    try {
      await deleteRunningPrediction(predictionId);
      setPredictionsRows(prev => prev.filter(p => p.prediction_session_id !== predictionId));
      setSelectionMap(prev =>
        prev.predictions === predictionId ? { ...prev, predictions: null } : prev
      );
      setSnack(t('predictionDeleted', 'Prediction deleted'));
    } catch {
      setError(t('failedToDeletePrediction', 'Failed to delete prediction'));
    } finally {
      setDeletingPredictionIds(prev => prev.filter(id => id !== predictionId));
    }
  };

  // Share the selected prediction to a club — same as the "Share to Club" button on the prediction
  // analysis page (POST /clubs/{clubId}/predictions).
  const handleSharePredictionToClub = async (clubId, club) => {
    if (!sharePredictionRow) return;
    setShareBusy(true);
    try {
      await shareClubPrediction(clubId, sharePredictionRow.prediction_session_id);
      setSnack(t('predictionSharedToClub', { club: club?.name || 'club', defaultValue: `Prediction shared to ${club?.name || 'club'}` }));
      setSharePredictionRow(null);
    } catch (err) {
      setSnack(err?.response?.data?.detail || t('failedSharePrediction', 'Failed to share prediction'));
    } finally {
      setShareBusy(false);
    }
  };

  const handleDeleteMyFile = async (fileId, e) => {
    e.stopPropagation();
    if (deletingFileIds.includes(fileId)) return;
    setDeletingFileIds(prev => [...prev, fileId]);
    setError(null);
    const filename = myFiles.find(f => f.id === fileId)?.filename;
    try {
      await deleteMyRunningFile('fit', fileId);
      setMyFiles(prev => prev.filter(f => f.id !== fileId));
      // The backend also drops it from the saved multi-file selection; mirror that locally so a
      // later toggle in this same session doesn't send the now-dangling id back and get rejected.
      setSecondaryRefIds(prev => prev.filter(id => id !== fileId));
      setSelectionMap(prev =>
        prev.myFiles === fileId ? { ...prev, myFiles: null } : prev
      );
      setSnack(filename
        ? t('fileDeletedNamed', { file: filename, defaultValue: `Deleted ${filename}` })
        : t('fileDeleted', 'File deleted'));
    } catch {
      setError(t('failedToDeleteFile', 'Failed to delete file'));
    } finally {
      setDeletingFileIds(prev => prev.filter(id => id !== fileId));
    }
  };

  // Race prediction over the marked runs. With nothing marked there is nothing
  // to predict from, so the press points at the step it is missing rather than
  // doing nothing or failing later on the race picker.
  const handleRacePredictClick = () => {
    if (secondaryRefIds.length === 0) { pulseMarkColumn(); return; }
    onClose?.();
    onMultiFilePredict();
  };

  const toggleSecondaryRef = async (fileId, e) => {
    if (e) e.stopPropagation();
    if (secondaryRefSaving) return;
    setMarkColHighlight(false);
    const isSelected = secondaryRefIds.includes(fileId);
    let next;
    if (isSelected) {
      next = secondaryRefIds.filter(id => id !== fileId);
    } else {
      if (secondaryRefIds.length >= 4) {
        // Replace the oldest (first) selection when adding a 5th
        next = [...secondaryRefIds.slice(1), fileId];
      } else {
        next = [...secondaryRefIds, fileId];
      }
    }
    const prev = secondaryRefIds;
    setSecondaryRefIds(next);        // optimistic
    setSecondaryRefSaving(true);
    try {
      const res = await updateRunningProfile(next);
      if (Array.isArray(res?.secondary_fit_file_ids)) {
        setSecondaryRefIds(res.secondary_fit_file_ids);
      }
    } catch {
      setSecondaryRefIds(prev);      // revert on failure
      setError(t('failedToSaveSecondaryRef', 'Failed to update secondary reference files'));
    } finally {
      setSecondaryRefSaving(false);
    }
  };

  const refreshMyFiles = ({ resetCommunity = false } = {}) => {
    const loader = allowShareMyFiles ? getUserRunningFitFilesWithSharing : getMyRunningFitFiles;
    setMyFilesLoading(true);
    loader()
      .then(data => setMyFiles(Array.isArray(data) ? data : data.files || []))
      .catch(() => setError(t('failedToLoadFiles', 'Failed to load your files')))
      .finally(() => setMyFilesLoading(false));
    if (resetCommunity) setCommunityLoaded(false);
  };

  const handleUnshareMyFile = async (sharedWorkoutId, e) => {
    e.stopPropagation();
    try {
      await unshareRunningWorkout(sharedWorkoutId);
      refreshMyFiles({ resetCommunity: true });
    } catch {
      setError(t('failedToUnshare', 'Failed to unshare workout'));
    }
  };

  // ── Selection / confirm ───────────────────────────────────────────────
  const selectedId = selectionMap[currentTabId] ?? null;

  const canConfirm =
    (currentTabId === 'upload' && !!uploadFile) ||
    (currentTabId === 'gpxUpload' && !!gpxUploadFile) ||
    (currentTabId !== 'upload' && currentTabId !== 'gpxUpload' && selectedId !== null);

  // Which files fed the prediction. A single-reference prediction names one; a
  // deep one leads with its main reference, a multi-file one lists all of them.
  const referenceFilesColumn = {
    field: 'reference_files', headerName: t('referenceFit', 'Reference FIT'),
    flex: 2.8, minWidth: 200, sortable: false,
    renderCell: (params) => {
      const files = params.value && params.value.length
        ? params.value : [params.row?.fit_filename].filter(Boolean);
      if (!files.length) return <Typography variant="caption" color={colors.grey[500]}>—</Typography>;
      // All of them, named. Dropping the Race and Dist columns in the single-race
      // layout freed the width, and "first +1" withheld a name we already have.
      return (
        <Typography
          variant="caption" color={colors.grey[200]} noWrap title={files.join('\n')}
          sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
        >
          {files.join(', ')}
        </Typography>
      );
    },
  };

  // 'deep' and 'multi-file' are different algorithms and used to look identical
  // here, because the stored deep flag is set for both.
  const predictionKindColumn = {
    field: 'prediction_kind', headerName: t('type', 'Type'), width: 104, sortable: true,
    renderCell: (params) => {
      const kind = params.value || 'single';
      const label = kind === 'multi-file' ? t('multiFile', 'multi-file')
        : kind === 'deep' ? t('deep', 'deep')
        : t('single', 'single');
      return (
        <Chip
          label={label}
          size="small"
          title={kind === 'multi-file'
            ? t('multiFileHint', 'Every reference file weighted equally')
            : kind === 'deep'
              ? t('deepHint', 'One main reference; the others only cover terrain')
              : t('singleHint', 'One reference run')}
          sx={{
            height: 20, fontSize: '0.65rem',
            backgroundColor: kind === 'single' ? colors.grey[700] : colors.blueAccent[700],
            color: colors.grey[100],
          }}
        />
      );
    },
  };

  const predictionsColumns = [
    ...(predictionsSingleRace
      ? [referenceFilesColumn, predictionKindColumn]
      : [{ field: 'race_name', headerName: t('race', 'Race'), flex: 2, minWidth: 140 },
         referenceFilesColumn, predictionKindColumn]),
    ...(predictionsSingleRace ? [] : [{
      field: 'race_distance_km', headerName: t('dist', 'Dist'), width: 75,
      valueFormatter: (v) => v != null ? `${v.toFixed(1)} km` : '-',
    }]),
    {
      field: 'predicted_total_time_s', headerName: t('predTime', 'Pred. Time'), width: 100,
      valueFormatter: (v) => {
        if (!v) return '-';
        const h = Math.floor(v / 3600);
        const m = Math.floor((v % 3600) / 60);
        return `${h}h ${String(m).padStart(2, '0')}m`;
      },
    },
    {
      field: 'created_at', headerName: t('date', 'Date'), width: 132,
      // Several predictions for one race on one day are common, so the time is
      // what separates them.
      valueFormatter: (v) => v ? new Date(v).toLocaleString() : '-',
    },
    {
      field: '_share', headerName: '', width: 40, sortable: false, filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setSharePredictionRow(params.row); }}
          sx={{ color: colors.greenAccent[400] }}
          title={t('shareToClub', 'Share to Club')}
        >
          <ShareIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: '_delete', headerName: '', width: 40, sortable: false, filterable: false,
      renderCell: (params) => {
        const busyRow = deletingPredictionIds.includes(params.row.prediction_session_id);
        return (
          <IconButton
            size="small"
            disabled={busyRow}
            onClick={(e) => handleDeletePrediction(params.row.prediction_session_id, e)}
            sx={{ color: colors.redAccent[400] }}
            title={busyRow
              ? t('deletingPrediction', 'Deleting…')
              : t('deletePrediction', 'Delete prediction')}
          >
            {busyRow
              ? <CircularProgress size={16} sx={{ color: colors.redAccent[400] }} />
              : <DeleteIcon fontSize="small" />}
          </IconButton>
        );
      },
    },
  ];

  const clubPredictionsColumns = [
    {
      field: 'sharer_email', headerName: t('member', 'Member'), width: 160,
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value || ''} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'club_name', headerName: t('club', 'Club'), width: 110,
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value || ''} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    ...(predictionsSingleRace
      ? [referenceFilesColumn, predictionKindColumn]
      : [{ field: 'race_name', headerName: t('race', 'Race'), flex: 2, minWidth: 140 },
         referenceFilesColumn, predictionKindColumn]),
    ...(predictionsSingleRace ? [] : [{
      field: 'race_distance_km', headerName: t('dist', 'Dist'), width: 75,
      valueFormatter: (v) => v != null ? `${v.toFixed(1)} km` : '-',
    }]),
    {
      field: 'predicted_total_time_s', headerName: t('predTime', 'Pred. Time'), width: 100,
      valueFormatter: (v) => {
        if (!v) return '-';
        const h = Math.floor(v / 3600);
        const m = Math.floor((v % 3600) / 60);
        return `${h}h ${String(m).padStart(2, '0')}m`;
      },
    },
    {
      field: 'shared_at', headerName: t('date', 'Date'), width: 132,
      valueFormatter: (v) => v ? new Date(v).toLocaleString() : '-',
    },
  ];

  const buildSource = (tabId, rowId) => {
    if (tabId === 'myFiles') {
      const record = myFiles.find(f => f.id === rowId);
      return { type: 'myfile', fileId: rowId, record };
    }
    if (tabId === 'community') {
      const record = communityRows.find(r => r.id === rowId);
      return { type: 'community', sharedId: rowId, record };
    }
    if (tabId === 'races') {
      const record = racesRows.find(r => r.id === rowId);
      return { type: 'race', raceId: rowId, record };
    }
    if (tabId === 'predictions') {
      const record = predictionsRows.find(r => r.id === rowId);
      return { type: 'prediction', predictionId: record?.prediction_session_id, record };
    }
    if (tabId === 'clubPredictions') {
      const record = clubPredictionsRows.find(r => r.id === rowId);
      return { type: 'prediction', predictionId: record?.prediction_session_id, record };
    }
    const extra = extraTabs.find(t => t.id === tabId);
    if (extra) {
      const row = (extra.rows || []).find(r => r.id === rowId);
      // A self-rendering tab keeps its own data, so there is no row here to
      // look up — hand getSource the id and let the tab resolve the rest.
      if (!row && typeof extra.render === 'function') {
        return extra.getSource({ id: rowId });
      }
      return row ? extra.getSource(row) : null;
    }
    return null;
  };

  const handleConfirm = () => {
    if (currentTabId === 'upload') {
      if (!uploadFile) return;
      onSelect({ type: 'upload', file: uploadFile });
      return;
    }
    if (currentTabId === 'gpxUpload') {
      if (!gpxUploadFile) return;
      onSelect({ type: 'gpxUpload', file: gpxUploadFile });
      return;
    }
    const source = buildSource(currentTabId, selectedId);
    if (source) onSelect(source);
  };

  const handleRowDoubleClick = (params, tabId) => {
    // MUI passes the row id as params.id; params.row.id also holds it. Use both defensively.
    const rowId = params?.row?.id ?? params?.id;
    if (rowId == null) return;
    // Mirror the single-click selection so state stays consistent, then start.
    setSelectionMap(prev => ({ ...prev, [tabId]: rowId }));
    const source = buildSource(tabId, rowId);
    if (source) onSelect(source);
  };

  // ── Column definitions ────────────────────────────────────────────────
  const myFilesColumns = [
    ...(allowMarkSecondaryRef ? [{
      field: '_secondaryRef',
      headerName: t('secondaryRefColumn', 'Multi-file'),
      width: 78,
      sortable: false, filterable: false,
      cellClassName: 'mark-col-cell',
      headerClassName: 'mark-col-header',
      renderCell: (params) => {
        const selected = secondaryRefIds.includes(params.row.id);
        return (
          <IconButton
            size="small"
            disabled={secondaryRefSaving}
            onClick={(e) => toggleSecondaryRef(params.row.id, e)}
            sx={{ color: selected ? colors.greenAccent[400] : colors.grey[500] }}
            title={selected
              ? t('unmarkSecondaryRef', 'Do not use this run for race predictions')
              : t('markSecondaryRef', 'Use this run for race predictions')}
          >
            {selected ? <BookmarkAddedIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
          </IconButton>
        );
      },
    }] : []),
    {
      field: 'filename',
      headerName: t('fileColumn', 'File'),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', overflow: 'hidden' }}>
          <Typography variant="body2" color={colors.grey[100]} fontWeight={500} noWrap title={params.row.filename}>
            {params.row.filename}
          </Typography>
          {/* This list is an activity feed, so Garmin-sourced rows must carry
              the device credit here rather than only on the detail view. */}
          {params.row.source === 'garmin' && (
            <Typography variant="caption" color={colors.grey[400]} noWrap sx={{ fontSize: 10.5 }}>
              {garminDeviceLabel(params.row.device_name)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: t('description', 'Description'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value}>{params.value}</Typography>
        : <Typography variant="caption" color={colors.grey[600]}>—</Typography>,
    },
    {
      field: 'location_name',
      headerName: '📍 Location',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.blueAccent[400]} noWrap title={params.value}>{params.value}</Typography>
        : <Typography variant="caption" color={colors.grey[600]}>—</Typography>,
    },
    {
      field: 'race_name',
      headerName: '🏁 Race',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.greenAccent[400]} noWrap title={params.value}>{params.value}</Typography>
        : <Typography variant="caption" color={colors.grey[600]}>—</Typography>,
    },
    {
      field: 'workout_date', headerName: t('date', 'Date'), width: 90,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      field: 'total_distance', headerName: t('dist', 'Dist'), width: 80,
      valueFormatter: (v) => v ? formatDistance(v, 'metric') : '-',
    },
    {
      field: 'avg_pace', headerName: t('pace', 'Pace'), width: 80,
      valueFormatter: (v) => v ? formatPace(v, 'metric') : '-',
    },
    {
      field: 'elevation_gain', headerName: t('elevGainSymbol', '↑'), width: 60,
      valueFormatter: (v) => v ? `+${Math.round(v)}m` : '-',
    },
    ...(allowShareMyFiles ? [{
      field: '_share', headerName: t('sharedColumn', 'Shared'), width: 110,
      sortable: false, filterable: false,
      renderCell: (params) => params.row.is_shared ? (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            label="Shared"
            size="small"
            sx={{ height: 18, fontSize: '0.65rem', bgcolor: colors.greenAccent[800], color: colors.greenAccent[300], px: 0.5 }}
          />
          <IconButton
            size="small"
            onClick={(e) => handleUnshareMyFile(params.row.shared_workout_id, e)}
            sx={{ color: colors.redAccent[400] }}
            title={t('unshare', 'Unshare')}
          >
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setShareDialogFile(params.row); }}
          sx={{ color: colors.blueAccent[400] }}
          title={t('shareWithCommunity', 'Share with community')}
        >
          <ShareIcon fontSize="small" />
        </IconButton>
      ),
    }] : []),
    ...(allowDeleteMyFiles ? [{
      field: '_delete', headerName: '', width: 40,
      sortable: false, filterable: false,
      renderCell: (params) => {
        const busyRow = deletingFileIds.includes(params.row.id);
        return (
          <IconButton
            size="small"
            disabled={busyRow}
            onClick={(e) => handleDeleteMyFile(params.row.id, e)}
            sx={{ color: colors.redAccent[400] }}
            title={busyRow ? t('deletingFile', 'Deleting…') : t('deleteFile', 'Delete file')}
          >
            {busyRow
              ? <CircularProgress size={16} sx={{ color: colors.redAccent[400] }} />
              : <DeleteIcon fontSize="small" />}
          </IconButton>
        );
      },
    }] : []),
  ];

  const communityColumns = useMemo(() => [
    {
      field: 'display_name', headerName: t('titleColumn', 'Title'), flex: 2, minWidth: 120,
      valueGetter: (value, row) => value || row.filename || `Workout #${row.id}`,
    },
    { field: 'owner_name', headerName: t('sharedBy', 'Shared by'), width: 100 },
    {
      field: 'race_name', headerName: t('race', 'Race'), flex: 1, minWidth: 100,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.greenAccent[400]} noWrap title={params.value}>{params.value}</Typography>
        : <Typography variant="caption" color={colors.grey[600]}>—</Typography>,
    },
    {
      field: 'shared_at', headerName: t('date', 'Date'), width: 90,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      field: 'finish_time_seconds', headerName: t('time', 'Time'), width: 80,
      valueFormatter: (v) => fmtFinishTime(v),
    },
    {
      field: 'avg_pace', headerName: t('pace', 'Pace'), width: 80,
      valueFormatter: (v) => v ? formatPace(v, 'metric') : '-',
    },
    {
      field: 'total_distance_km', headerName: t('dist', 'Dist'), width: 75,
      valueFormatter: (v) => v != null ? `${v.toFixed(1)} km` : '-',
    },
    {
      field: 'elevation_gain_m', headerName: t('elevGainSymbol', '↑'), width: 60,
      valueFormatter: (v) => v ? `+${Math.round(v)}m` : '-',
    },
  ], [colors, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const racesColumns = [
    {
      field: '_info', headerName: '', width: 40, sortable: false, filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setRaceDetailRow(params.row); }}
          sx={{ color: colors.blueAccent[400], p: 0.25 }}
          title={t('raceDetails', 'Race details')}
        >
          <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      ),
    },
    { field: 'event_name', headerName: t('eventName', 'Event'), flex: 1, minWidth: 100 },
    { field: 'name', headerName: t('race', 'Race'), flex: 2, minWidth: 140 },
    {
      field: 'distance_km', headerName: t('dist', 'Dist'), width: 75,
      valueFormatter: (v) => v != null ? `${v} km` : '-',
    },
    {
      field: 'elevation_gain_m', headerName: t('elevGainShort', '↑ Elev'), width: 75,
      valueFormatter: (v) => v != null ? `+${Math.round(v)} m` : '-',
    },
    { field: 'event_type', headerName: t('eventType', 'Event Type'), width: 100 },
    { field: 'country', headerName: t('country', 'Country'), width: 90 },
    {
      field: 'has_gpx', headerName: t('gpxColumn', 'GPX'), width: 55,
      renderCell: (params) => params.value
        ? <CheckCircleOutlineIcon sx={{ fontSize: '1rem', color: colors.greenAccent[400] }} />
        : null,
    },
  ];

  // ── DataGrid shared config ────────────────────────────────────────────
  const gridSx = {
    height: '100%',
    border: 'none',
    fontSize: '0.8rem',
    '& .MuiDataGrid-row': { cursor: 'pointer' },
    '& .MuiDataGrid-row.picker-row-selected': {
      backgroundColor: `${colors.blueAccent[900]} !important`,
    },
    '& .MuiDataGrid-row.picker-row-selected:hover': {
      backgroundColor: `${colors.blueAccent[800]} !important`,
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
    '@keyframes markColPulse': {
      '0%, 100%': { backgroundColor: 'transparent' },
      '50%': { backgroundColor: pulse.tint },
    },
    ...(markColHighlight ? {
      '& .mark-col-cell, & .mark-col-header': {
        animation: pulseAnimation('markColPulse'),
      },
      // The icons themselves are the target, so colour them for the duration —
      // an empty bookmark in grey is easy to read as a disabled control.
      '& .mark-col-cell .MuiSvgIcon-root': { color: pulse.edge },
      '@media (prefers-reduced-motion: reduce)': {
        '& .mark-col-cell, & .mark-col-header': {
          animation: 'none',
          backgroundColor: pulse.tint,
        },
      },
    } : {}),
  };

  const renderGrid = (rows, columns, tabId, loading, showToolbar = true, gridKey = undefined) => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (!loading && rows.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Typography color={colors.grey[400]}>{t('noItemsFound', 'No items found')}</Typography>
        </Box>
      );
    }
    const sel = selectionMap[tabId];
    return (
      <DataGrid
        key={gridKey}
        rows={rows}
        columns={columns}
        density="compact"
        getRowClassName={(params) => params.row.id === sel ? 'picker-row-selected' : ''}
        onRowClick={(params) =>
          setSelectionMap(prev => ({ ...prev, [tabId]: params.row.id }))
        }
        onRowDoubleClick={(params) => handleRowDoubleClick(params, tabId)}
        slots={showToolbar ? { toolbar: SearchToolbar } : {}}
        disableColumnMenu
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        pageSizeOptions={[25, 50, 100]}
        sx={gridSx}
      />
    );
  };

  // Marking files and then predicting with them were two steps in two places:
  // mark here, close, find the section on the dashboard. The files are chosen
  // right here, so the action belongs here too. Built once and placed either
  // beside the dismiss/Select pair or on its own row above them — both of those
  // are fullWidth, so letting the row wrap would stack all three.
  const racePredictButton = onMultiFilePredict && allowMarkSecondaryRef
    && currentTabId === 'myFiles'
    ? (
      <Button
        variant="outlined"
        startIcon={<GroupsIcon />}
        onClick={handleRacePredictClick}
        fullWidth={isMobile}
        sx={{
          flexShrink: 0,
          whiteSpace: 'nowrap',
          color: colors.grey[100],
          borderColor: colors.blueAccent[500],
          textTransform: 'none',
          '&:hover': { borderColor: colors.blueAccent[400] },
        }}
      >
        {t('racePredict', 'Race prediction')}
        {secondaryRefIds.length > 0 && ` (${secondaryRefIds.length})`}
      </Button>
    )
    : null;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '80%',
          backgroundColor: colors.grey[800],
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex" alignItems="center" justifyContent="space-between"
        px={2} py={1.5}
        sx={{ flexShrink: 0, borderBottom: `1px solid ${colors.grey[700]}` }}
      >
        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.grey[300] }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${colors.grey[700]}`,
          '& .MuiTab-root': { color: colors.grey[300] },
          '& .Mui-selected': { color: colors.blueAccent[400] },
          '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[400] },
        }}
      >
        {allTabs.map(tab => <Tab key={tab.id} label={tab.label} />)}
      </Tabs>

      {/* Content */}
      <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
        {error && (
          <Alert severity="error" sx={{ mx: 1, mt: 1, flexShrink: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Upload tab */}
        {currentTabId === 'upload' && (
          <Box p={2} flex={1} overflow="auto">
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragOver ? colors.blueAccent[400] : colors.grey[600]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? colors.grey[700] : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: colors.blueAccent[400], mb: 1 }} />
              <Typography variant="body1" color={colors.grey[200]}>
                {uploadFile
                  ? uploadFile.name
                  : t('dropFitHere', 'Drop a .fit file here or click to browse')}
              </Typography>
              {uploadFile && (
                <Typography variant="caption" color={colors.grey[400]}>
                  {(uploadFile.size / 1024).toFixed(0)} KB
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".fit"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </Box>
        )}

        {/* GPX Upload tab */}
        {currentTabId === 'gpxUpload' && (
          <Box p={2} flex={1} overflow="auto">
            <Box
              onDragOver={(e) => { e.preventDefault(); setGpxDragOver(true); }}
              onDragLeave={() => setGpxDragOver(false)}
              onDrop={handleGpxDrop}
              onClick={() => gpxInputRef.current?.click()}
              sx={{
                border: `2px dashed ${gpxDragOver ? colors.blueAccent[400] : colors.grey[600]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: gpxDragOver ? colors.grey[700] : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: colors.blueAccent[400], mb: 1 }} />
              <Typography variant="body1" color={colors.grey[200]}>
                {gpxUploadFile
                  ? gpxUploadFile.name
                  : t('dropGpxHere', 'Drop a .gpx file here or click to browse')}
              </Typography>
              {gpxUploadFile && (
                <Typography variant="caption" color={colors.grey[400]}>
                  {(gpxUploadFile.size / 1024).toFixed(0)} KB
                </Typography>
              )}
            </Box>
            <input
              ref={gpxInputRef}
              type="file"
              accept=".gpx"
              style={{ display: 'none' }}
              onChange={handleGpxInput}
            />
          </Box>
        )}

        {/* My Files tab */}
        {currentTabId === 'myFiles' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {/* flex-start, not center: this text wraps to several lines on a
                phone and a vertically centred icon then floats mid-sentence. */}
            {allowMarkSecondaryRef && (
              <Box sx={{ px: 1, pb: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <BookmarkAddedIcon sx={{ fontSize: '1rem', color: colors.greenAccent[400], flexShrink: 0, mt: '2px' }} />
                <Typography variant="caption" color={colors.grey[300]}>
                  {t('secondaryRefCount', 'Mark the runs to predict a race from ({{n}}/4)', { n: secondaryRefIds.length })}
                  {secondaryRefIds.length < 4 && (
                    <> — {t('secondaryRefHint', 'pick races of different distances to sharpen predictions at longer / shorter races')}</>
                  )}
                  {/* Marking a file here does nothing on its own, so say what to
                      press next. The button below does it without leaving. */}
                  {onMultiFilePredict && secondaryRefIds.length === 1 && (
                    <>{'. '}{t('secondaryRefOneMarked',
                      'Mark a second run for a steadier prediction, or press Race prediction below to go with this one.')}</>
                  )}
                  {onMultiFilePredict && secondaryRefIds.length > 1 && (
                    <>{'. '}{t('secondaryRefThen', 'Then press Race prediction below to choose a race.')}</>
                  )}
                </Typography>
              </Box>
            )}
            {renderGrid(myFiles, myFilesColumns, 'myFiles', myFilesLoading)}
          </Box>
        )}

        {/* Community tab */}
        {currentTabId === 'community' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(communityRows, communityColumns, 'community', communityLoading)}
          </Box>
        )}

        {/* Races tab */}
        {currentTabId === 'races' && (() => {
          const countries = [...new Set(racesRows.map(r => r.country).filter(Boolean))].sort();
          const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
          const q = norm(racesSearch);
          const filtered = racesRows.filter(r => {
            if (racesCountry && r.country !== racesCountry) return false;
            if (q && !norm(r.name).includes(q) && !norm(r.event_name).includes(q)) return false;
            return true;
          });
          const selectSx = {
            color: colors.grey[100],
            '.MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.blueAccent[500] },
            '.MuiSvgIcon-root': { color: colors.grey[300] },
          };
          return (
            <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
              {/* Filter bar — same style as bike community pages */}
              <Box display="flex" gap={2} flexWrap="wrap" px={1} pt={1} mb={2} flexShrink={0}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: colors.grey[300] }}>{t('country', 'Country')}</InputLabel>
                  <Select
                    value={racesCountry}
                    label={t('country', 'Country')}
                    onChange={(e) => setRacesCountry(e.target.value)}
                    sx={selectSx}
                  >
                    <MenuItem value="">{t('all', 'All')}</MenuItem>
                    {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder={t('search', 'Search...')}
                  value={racesSearch}
                  onChange={(e) => setRacesSearch(e.target.value)}
                  inputRef={racesSearchRef}
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      color: colors.grey[100],
                      '& fieldset': { borderColor: colors.grey[600] },
                      '&:hover fieldset': { borderColor: colors.blueAccent[500] },
                      '&.Mui-focused fieldset': { borderColor: colors.blueAccent[500] },
                    },
                    '& .MuiInputBase-input::placeholder': { color: colors.grey[400] },
                  }}
                />
              </Box>
              {renderGrid(filtered, racesColumns, 'races', racesLoading, false, `${racesCountry}|${racesSearch}`)}
            </Box>
          );
        })()}

        {/* Predictions tab */}
        {currentTabId === 'predictions' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(predictionsRows, predictionsColumns, 'predictions', predictionsLoading)}
          </Box>
        )}

        {/* Club Predictions tab */}
        {currentTabId === 'clubPredictions' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(clubPredictionsRows, clubPredictionsColumns, 'clubPredictions', clubPredictionsLoading)}
          </Box>
        )}

        {/* Extra tabs */}
        {extraTabs.map(extra =>
          currentTabId === extra.id && (
            <Box key={extra.id} display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
              {/* A tab may draw itself instead of using the grid — the Garmin
                  calendar is a month view, not a table. It still reports its
                  selection through selectionMap, so the footer's Select button
                  and getSource() work exactly as they do for a grid. */}
              {typeof extra.render === 'function'
                ? extra.render({
                    selectedId: selectionMap[extra.id],
                    onSelect: (id) =>
                      setSelectionMap(prev => ({ ...prev, [extra.id]: id })),
                    onConfirm: (id) => {
                      setSelectionMap(prev => ({ ...prev, [extra.id]: id }));
                      const source = extra.getSource?.(
                        (extra.rows || []).find(r => r.id === id) || { id }
                      );
                      if (source) onSelect(source);
                    },
                  })
                : renderGrid(extra.rows || [], extra.columns || [], extra.id, extra.loading || false)}
            </Box>
          )
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ flexShrink: 0, borderTop: `1px solid ${colors.grey[700]}`, p: 2 }}>
        {footer}
        {/* On a phone the third button gets its own row above the other two,
            which stay side by side. */}
        {isMobile && racePredictButton && (
          <Box mt={footer ? 1.5 : 0}>{racePredictButton}</Box>
        )}
        <Box display="flex" gap={1.5} mt={(footer || (isMobile && racePredictButton)) ? 1.5 : 0}>
          {!isMobile && racePredictButton}
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth
            startIcon={dismissLabel ? <CloseIcon /> : null}
            sx={{
              color: colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': { borderColor: colors.grey[300] },
            }}
          >
            {dismissLabel || t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!canConfirm}
            fullWidth
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.blueAccent[500] },
              '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
            }}
          >
            {actionLabel}
          </Button>
        </Box>
      </Box>
    </Drawer>

    <ShareWorkoutDialog
      open={!!shareDialogFile}
      onClose={() => setShareDialogFile(null)}
      file={shareDialogFile}
      onShared={() => refreshMyFiles({ resetCommunity: true })}
    />

    <ShareToClubDialog
      open={!!sharePredictionRow}
      onClose={() => setSharePredictionRow(null)}
      onPick={handleSharePredictionToClub}
      busy={shareBusy}
      title={t('sharePredictionToClub', 'Share Prediction to Club')}
      subtitle={sharePredictionRow ? (sharePredictionRow.race_name || t('prediction', 'Prediction')) : null}
    />

    <Snackbar
      open={!!snack}
      autoHideDuration={3000}
      onClose={() => setSnack('')}
      message={snack}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />

    <RaceDetailDialog
      race={raceDetailRow}
      open={!!raceDetailRow}
      onClose={() => setRaceDetailRow(null)}
    />
    </>
  );
}
